import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function About() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-doboku.com';
  
  useSEO({
    title: '会社概要 - おかやま土木工事株式会社 | 岡山市・倉敷市の土木工事',
    description: 'おかやま土木工事株式会社の会社概要。2000年設立、岡山市・倉敷市を中心に土木工事、水道工事、舗装工事を手がける地域密着企業。代表取締役：岡山太郎、従業員数5名。確かな技術と丁寧な施工で地域のインフラ整備に貢献しています。',
    keywords: '会社概要,おかやま土木工事,岡山市,倉敷市,土木工事会社',
    canonical: '/about',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "おかやま土木工事株式会社",
      "url": siteUrl,
      "logo": `${siteUrl}/logo.png`,
      "foundingDate": "2000",
      "founder": {
        "@type": "Person",
        "name": "岡山太郎"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "○○町1-2-3",
        "addressLocality": "岡山市北区",
        "addressRegion": "岡山県",
        "addressCountry": "JP"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "086-012-3456",
        "contactType": "customer service"
      }
    }
  });

  const companyHistory = [
    { year: '2000年', event: 'おかやま土木工事株式会社設立' },
    { year: '2003年', event: '岡山市内での実績拡大、水道工事部門強化' },
    { year: '2007年', event: '倉敷市エリアへのサービス拡大' },
    { year: '2012年', event: '舗装工事専門チーム設立' },
    { year: '2018年', event: '環境配慮型工法の導入開始' },
    { year: '2023年', event: 'デジタル化推進、工事管理システム導入' }
  ];

  const certifications = [
    '建設業許可（土木工事業）',
    '建設業許可（管工事業）',
    '建設業許可（舗装工事業）',
    '1級土木施工管理技士',
    '給水装置工事主任技術者',
    '安全管理者',
    '建設業経理士',
    'ISO9001認証取得'
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-96 bg-cover bg-center" style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/157e9dabb126431b57320f5c818d7f5c.jpg')`
        }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="text-white text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">会社概要</h1>
                <p className="text-xl md:text-2xl">
                  地域密着で20年以上、岡山県の発展に貢献し続けています
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  <strong>私たちについて</strong>
                </h2>
                <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                  <p>
                    おかやま土木工事株式会社は、2000年の設立以来、岡山市・倉敷市を中心とした岡山県内で、
                    土木工事・水道工事・舗装工事を専門に手がけてまいりました。
                  </p>
                  <p>
                    「安全第一、品質第一、お客様第一」をモットーに、地域のインフラ整備に貢献し、
                    お客様に満足いただける工事を提供することを使命としています。
                  </p>
                  <p>
                    経験豊富な技術者と最新の設備により、小規模な修繕工事から大規模な造成工事まで、
                    幅広いニーズにお応えしています。
                  </p>
                  <p>
                    これからも地域密着企業として、岡山県の発展と住民の皆様の安全・安心な暮らしのために、
                    全力で取り組んでまいります。
                  </p>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20construction%20company%20team%20photo%20in%20Okayama%2C%20professional%20group%20of%20construction%20workers%20and%20management%20staff%2C%20safety%20helmets%20and%20work%20uniforms%2C%20company%20building%20background%2C%20confident%20and%20reliable%20team%2C%20bright%20daylight%2C%20high%20quality%20corporate%20photography&width=600&height=500&seq=about_team&orientation=landscape"
                  alt="会社チーム"
                  className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Company Information */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>会社情報</strong>
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">基本情報</h3>
                <div className="space-y-4">
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">会社名</div>
                    <div className="text-gray-600">おかやま土木工事株式会社</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">代表取締役</div>
                    <div className="text-gray-600">岡山 太郎</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">設立</div>
                    <div className="text-gray-600">2000年</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">従業員数</div>
                    <div className="text-gray-600">5名</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">所在地</div>
                    <div className="text-gray-600">
                      〒700-0000<br />
                      岡山市北区○○町1-2-3
                    </div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">電話番号</div>
                    <div className="text-gray-600">086-012-3456</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">FAX</div>
                    <div className="text-gray-600">086-012-3457</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">営業時間</div>
                    <div className="text-gray-600">9:00〜18:00</div>
                  </div>
                  <div className="flex">
                    <div className="w-32 font-semibold text-gray-700">定休日</div>
                    <div className="text-gray-600">日曜日・祝日</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">事業内容</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <i className="ri-check-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">土木工事業</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">管工事業（水道工事）</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">舗装工事業</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">外構工事</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">造成工事</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">メンテナンス・修繕工事</span>
                  </div>
                </div>

                <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">サービスエリア</h4>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <i className="ri-map-pin-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">岡山市（北区・中区・東区・南区）</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-map-pin-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">倉敷市</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-map-pin-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">岡山県内その他地域（要相談）</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company History */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>沿革</strong>
            </h2>
            
            <div className="space-y-8">
              {companyHistory.map((item, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mr-6">
                    <span className="text-blue-600 font-bold text-sm">{item.year}</span>
                  </div>
                  <div className="flex-1 pt-6">
                    <p className="text-lg text-gray-700">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>資格・許可・認証</strong>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-md">
                  <div className="flex items-center">
                    <i className="ri-award-line text-blue-600 mr-3"></i>
                    <span className="text-gray-700">{cert}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Access */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              <strong>アクセス</strong>
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">所在地</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-blue-600 mr-3 mt-1"></i>
                      <div>
                        <div className="font-semibold text-gray-900">おかやま土木工事株式会社</div>
                        <div className="text-gray-600">
                          〒700-0000<br />
                          岡山市北区○○町1-2-3
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-phone-line text-blue-600 mr-3"></i>
                      <div>
                        <div className="font-semibold text-gray-900">TEL: 086-012-3456</div>
                        <div className="text-gray-600">営業時間: 9:00〜18:00</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-car-line text-blue-600 mr-3"></i>
                      <div className="text-gray-600">
                        岡山駅から車で約15分<br />
                        駐車場完備
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.8!2d133.9341!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU2JzAyLjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg shadow-lg"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}